import { collection, doc, setDoc, addDoc, updateDoc, deleteDoc, getDoc, getDocs, query, where, serverTimestamp, onSnapshot } from 'firebase/firestore'; 
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage'; 
import { db, storage } from './firebase'; 

// ==========================================
// UTILITIES
// ==========================================

// Utility to generate custom Dealer IDs (e.g., DLR-8924)
const generateDealerID = () => {
  const randomNum = Math.floor(1000 + Math.random() * 9000);
  return `DLR-${randomNum}`;
};

// ==========================================
// TENANT & ROUTING LOGIC
// ==========================================

export const getUserProfile = async (uid) => {
  const userRef = doc(db, 'users', uid);
  const userSnap = await getDoc(userRef);
  if (userSnap.exists()) {
    return userSnap.data();
  }
  throw new Error('User profile not found in global directory.');
};

// ==========================================
// PIPELINE & DEAL ENGINE
// ==========================================

export const createNewLead = async (masterDealerId, repUid, repName, leadData) => {
  try {
    const newDealerId = generateDealerID();
    // ALIGNED TO NEW SCHEMA: crm_leads
    const docRef = doc(db, `clients/${masterDealerId}/crm_leads`, newDealerId);
    
    await setDoc(docRef, {
      ...leadData,
      dealerId: newDealerId,
      rep_uid: repUid,
      rep_name: repName,
      status: "New Lead",
      stage: "newLeads",
      authProvisioned: false, 
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      lastTouch: serverTimestamp()
    });

    console.log(`Successfully created isolated pipeline record for ${newDealerId}`);
    return newDealerId;
  } catch (e) {
    console.error("Error creating provision record: ", e);
    throw e;
  }
};

export const getPipelineDeals = async (masterDealerId, operatorUid = null) => {
  // ALIGNED TO NEW SCHEMA: crm_leads
  const pipelineRef = collection(db, `clients/${masterDealerId}/crm_leads`);
  
  let q = pipelineRef;
  if (operatorUid) {
    q = query(pipelineRef, where('rep_uid', '==', operatorUid));
  }

  const querySnapshot = await getDocs(q);
  const deals = [];
  querySnapshot.forEach((doc) => {
    deals.push({ id: doc.id, ...doc.data() });
  });
  
  return deals;
};

// ==========================================
// 1099 CONTRACTOR HR / COMPLIANCE
// ==========================================

export const onboardContractor = async (masterDealerId, contractorUid, payload) => {
  // ALIGNED TO NEW SCHEMA: crm_contractors
  const contractorRef = doc(db, `clients/${masterDealerId}/crm_contractors`, contractorUid);
  
  const formattedData = {
    first_name: payload.firstName,
    last_name: payload.lastName,
    email: payload.email,
    phone: payload.phone,
    location: payload.location || 'Unassigned',
    status: 'Active',
    commission_tier: 'Standard 1099',
    compliance: {
      w9: false,
      nda: false,
      ica: false
    },
    metrics: {
      ltv: 0,
      current_mrr: 0,
      retention_rate: 100,
      ytd_payout: 0
    },
    createdAt: serverTimestamp()
  };

  await setDoc(contractorRef, formattedData);
  return contractorRef.id;
};

export const getFleetContractors = async (masterDealerId) => {
  // ALIGNED TO NEW SCHEMA: crm_contractors
  const contractorsRef = collection(db, `clients/${masterDealerId}/crm_contractors`);
  const querySnapshot = await getDocs(contractorsRef);
  
  const fleet = [];
  querySnapshot.forEach((doc) => {
    fleet.push({ id: doc.id, ...doc.data() });
  });
  
  return fleet;
};

// ==========================================
// COMMISSION & PAYOUT ENGINE
// ==========================================

export const getCommissionRules = async (masterDealerId) => {
  const rulesRef = doc(db, `clients/${masterDealerId}/settings`, 'commission_rules');
  const snap = await getDoc(rulesRef);
  if (snap.exists()) return snap.data();
  return null;
};

export const saveCommissionRules = async (masterDealerId, rulesData) => {
  const rulesRef = doc(db, `clients/${masterDealerId}/settings`, 'commission_rules');
  await setDoc(rulesRef, {
    ...rulesData,
    updatedAt: serverTimestamp()
  }, { merge: true });
};

export const getOperatorCommissions = async (masterDealerId, operatorUid) => {
  const transRef = collection(db, `clients/${masterDealerId}/transactions`);
  const q = query(transRef, where('rep_uid', '==', operatorUid));
  const snap = await getDocs(q);
  
  const transactions = [];
  snap.forEach(doc => {
    transactions.push({ id: doc.id, ...doc.data() });
  });
  return transactions;
};

export const getPendingLiabilities = async (masterDealerId) => {
  const transRef = collection(db, `clients/${masterDealerId}/transactions`);
  const q = query(transRef, where('status', '==', 'Pending Stripe'));
  const snap = await getDocs(q);
  
  const liabilitiesMap = {};
  snap.forEach(doc => {
    const data = doc.data();
    const rep = data.rep_name || 'Unknown Rep';
    if (!liabilitiesMap[rep]) liabilitiesMap[rep] = { rep: rep, pending: 0, totalDeals: 0 };
    liabilitiesMap[rep].pending += (data.commission_amount || 0);
    liabilitiesMap[rep].totalDeals += 1;
  });
  return Object.values(liabilitiesMap);
};

// ==========================================
// TACTICAL OPERATIONS (TASK ENGINE)
// ==========================================

export const getDailyTasks = async (masterDealerId, uid, isOperatorView) => {
  const tasksRef = collection(db, `clients/${masterDealerId}/tasks`);
  // Removed strict where() query so we can manually catch unassigned web calendar blocks
  const snap = await getDocs(tasksRef);
  
  const tasks = [];
  snap.forEach(doc => {
    const data = doc.data();
    // Fetch if explicitly assigned, OR if it's an unassigned website meeting block
    if (data.uid === uid || (!data.uid && data.type === 'meeting')) {
      tasks.push({ id: doc.id, ...data });
    }
  });
  return tasks.sort((a, b) => (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0));
};

export const addDailyTask = async (masterDealerId, uid, isOperatorView, payload) => {
  const tasksRef = collection(db, `clients/${masterDealerId}/tasks`);
  const docRef = await addDoc(tasksRef, {
    ...payload,
    uid,
    isOperatorTask: isOperatorView,
    createdAt: serverTimestamp()
  });
  return docRef.id;
};

export const toggleTaskCompletion = async (masterDealerId, uid, taskId, isCompleted) => {
  const taskRef = doc(db, `clients/${masterDealerId}/tasks`, taskId);
  await updateDoc(taskRef, { 
    completed: isCompleted,
    updatedAt: serverTimestamp() 
  });
};

// ==========================================
// METRICS & PULSE ENGINE
// ==========================================

export const getSystemPulse = async (masterDealerId) => {
  // ALIGNED TO NEW SCHEMA: crm_contractors and crm_leads
  const fleetRef = collection(db, `clients/${masterDealerId}/crm_contractors`);
  const fleetSnap = await getDocs(fleetRef);

  const pipelineRef = collection(db, `clients/${masterDealerId}/crm_leads`);
  const tenantQuery = query(pipelineRef, where('stage', '==', 'closedWon'));
  const tenantSnap = await getDocs(tenantQuery);

  return {
    totalFleet: fleetSnap.size || 0,
    activeTenants: tenantSnap.size || 0,
    pendingApprovals: 0 
  };
};

export const getOperatorPulse = async (masterDealerId, uid) => {
  // ALIGNED TO NEW SCHEMA: crm_leads
  const pipelineRef = collection(db, `clients/${masterDealerId}/crm_leads`);
  // Removed strict where() query so we can manually catch and count unassigned web volume
  const pipeSnap = await getDocs(pipelineRef);

  let activeClients = 0;
  let dealsInPipeline = 0;

  pipeSnap.forEach(doc => {
    const data = doc.data();
    // Catch if explicitly assigned, OR if it is an unassigned website checkout lead
    if (data.rep_uid === uid || !data.rep_uid) {
      const rawStatus = data.stage || data.status || 'newLeads';
      const stage = (rawStatus === 'Paid Lead - Unscheduled' || rawStatus === 'New Lead') ? 'newLeads' : rawStatus;

      if (stage === 'closedWon') {
        activeClients++;
      } else {
        dealsInPipeline++;
      }
    }
  });

  // ALIGNED TO NEW SCHEMA: crm_contractors
  const repRef = doc(db, `clients/${masterDealerId}/crm_contractors`, uid);
  const repSnap = await getDoc(repRef);
  
  let mtdCommission = 0;
  let pendingPayout = 0;
  let tier = 'Standard 1099';

  if (repSnap.exists()) {
    const data = repSnap.data();
    tier = data.commission_tier || tier;
    mtdCommission = data.metrics?.current_mrr || 0; 
    pendingPayout = data.metrics?.ytd_payout || 0;
  }

  return {
    tier,
    mtdCommission,
    pendingPayout,
    activeClients,
    dealsInPipeline
  };
};

// ==========================================
// ASSET VAULT ENGINE (CMS)
// ==========================================

export const getVaultAssets = async (masterDealerId) => {
  const assetsRef = collection(db, `clients/${masterDealerId}/assets`);
  const snap = await getDocs(assetsRef);
  const assets = [];
  snap.forEach(doc => {
    assets.push({ id: doc.id, ...doc.data() });
  });
  return assets.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
};

export const uploadVaultFile = async (masterDealerId, file) => {
  const storagePath = `clients/${masterDealerId}/vault_assets/${Date.now()}_${file.name}`;
  const storageRef = ref(storage, storagePath);
  
  await uploadBytes(storageRef, file);
  const fileUrl = await getDownloadURL(storageRef);
  
  return { fileUrl, storagePath };
};

export const addVaultAsset = async (masterDealerId, payload) => {
  const assetsRef = collection(db, `clients/${masterDealerId}/assets`);
  const docRef = await addDoc(assetsRef, {
    ...payload,
    downloads: 0,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
  return docRef.id;
};

export const updateVaultAsset = async (masterDealerId, assetId, payload) => {
  const assetRef = doc(db, `clients/${masterDealerId}/assets`, assetId);
  await updateDoc(assetRef, {
    ...payload,
    updatedAt: serverTimestamp()
  });
};

export const deleteVaultAsset = async (masterDealerId, assetId, storagePath) => {
  if (storagePath) {
    try {
      const fileRef = ref(storage, storagePath);
      await deleteObject(fileRef);
    } catch (e) {
      console.warn("Could not delete physical file, or it didn't exist.", e);
    }
  }
  
  const assetRef = doc(db, `clients/${masterDealerId}/assets`, assetId);
  await deleteDoc(assetRef);
};

export const incrementAssetDownload = async (masterDealerId, assetId, currentCount) => {
  const assetRef = doc(db, `clients/${masterDealerId}/assets`, assetId);
  await updateDoc(assetRef, {
    downloads: (currentCount || 0) + 1
  });
};

// ==========================================
// REAL-TIME DATA SYNC (MIGRATION & POS)
// ==========================================

export const subscribeToTenantCustomers = (tenantId, callback) => {
  if (!tenantId) return () => {}; 

  const customersRef = collection(db, `clients/${tenantId}/customers`);
  const q = query(customersRef); 

  const unsubscribe = onSnapshot(q, (snapshot) => {
    const customers = [];
    snapshot.forEach((doc) => {
      customers.push({ id: doc.id, ...doc.data() });
    });
    callback(customers);
  }, (error) => {
    console.error("Live Customer Sync Failed:", error);
  });

  return unsubscribe;
};

export const subscribeToTenantInventory = (tenantId, callback) => {
  if (!tenantId) return () => {};

  const inventoryRef = collection(db, `clients/${tenantId}/inventory`);
  const q = query(inventoryRef);

  const unsubscribe = onSnapshot(q, (snapshot) => {
    const inventory = [];
    snapshot.forEach((doc) => {
      inventory.push({ id: doc.id, ...doc.data() });
    });
    callback(inventory);
  }, (error) => {
    console.error("Live Inventory Sync Failed:", error);
  });

  return unsubscribe;
};